// ─────────────────────────────────────────────────────────────────────────────
// IMPORTS
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import "@splidejs/splide/dist/css/splide.min.css";
import {
  Wallet,
  Gamepad2,
  Sparkles,
  Trophy,
  ShieldCheck,
  UserCheck,
  Gift,
  Coins,
  CreditCard,
  Clock,
  Search,
  Play,
  Flame,
  ChevronRight,
  X,
  Maximize2,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Settings,
  Sun,
  Moon,
  Send,
  Plus,
} from "lucide-react";
import {
  selectChatMessages,
  selectChatLoading,
  selectChatActive,
  selectChatError,
  selectUserId,
  selectUserPhone,
  addMessage,
  clearChat,
  setIsActive,
  clearError,
  updateUserIdAndReload,
  reloadChatHistory,
  initializeWelcomeMessage,
  setSessionId,
  setMessages,
  selectChatOpenCount,
} from "../../chatbot/cl_ChatbotSlice";
import { fetchAccountDetails } from "../../accountDetails/cl_AccountDetailsSlice";
import { getRealBalance } from "../../signIn/cl_SignInSlice";
import { getFromLocalStorage, buildGameLaunchUrl } from "../../../services/helper/cl_Helper";
import "./AssistantChat.scss";

// ─────────────────────────────────────────────────────────────────────────────
// API CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────
const CHAT_API_ENDPOINT = process.env.REACT_APP_CHAT_API_ENDPOINT;
const LAUNCH_GAME_ENDPOINT = process.env.REACT_APP_HUB88_BASEPATH;
const DELETE_CHAT_HISTORY_API = process.env.REACT_APP_DELETE_CHAT_HISTORY_API;
// ─────────────────────────────────────────────────────────────────────────────
// UTILITY HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Global loadgame function - compatible with backend's onclick="return loadgame(true);"
window.loadgame = function(isMiniGame) {
  // The element that triggered the click is available through document's last clicked element
  // Since this is called from onclick, we need to get the element via a click handler
  // For now, just return the appropriate value - the actual handling is done via event delegation
  return !isMiniGame; // Return false for mini game (prevent navigation), true for normal game
};

// Format message content with proper sanitization
const formatMessageContent = (content) => {
  if (!content) return "";
  return DOMPurify.sanitize(content.toString());
};

// Helper function to format AI response HTML content
const EMOJI_LEADING_RE = /^[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}][\uFE0F\u20E3]?\s*/u;
const hasLeadingEmoji = (text) => EMOJI_LEADING_RE.test(text);
const stripLeadingEmoji = (text) => text.replace(EMOJI_LEADING_RE, '');

const formatAIResponseContent = (content) => {
  if (!content) return "";
  let f = content.toString()
    .replace(/<h([1-6])>(.*?)<\/h[1-6]>/gi, (_, level, text) => {
      const iconClass = hasLeadingEmoji(text) ? ' ai-heading-icon' : '';
      const cleanText = stripLeadingEmoji(text);
      return `<h${level} class="ai-heading ai-heading-${level}${iconClass}">${cleanText}</h${level}>`;
    })
    .replace(/<p>(.*?)<\/p>/gi, '<p class="ai-paragraph">$1</p>')
    .replace(/<strong>(.*?)<\/strong>/gi, '<strong class="ai-bold">$1</strong>')
    .replace(/<ul>(.*?)<\/ul>/gis, '<ul class="ai-list">$1</ul>')
    .replace(/<li>(.*?)<\/li>/gi, '<li class="ai-list-item">$1</li>')
    .replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (match, href, text) => {
      let updatedHref = href;
      let isMiniGameLink = match.includes('isMiniGame="true"');

      // For gamelaunch URLs, add returnpath
      if (href.includes('/gamelaunch')) {
        try {
          const url = new URL(href, window.location.origin);
          url.searchParams.set('returnpath', window.location.pathname);
          updatedHref = url.toString();
        } catch (_e) {}

        // If this is a mini game link, extract game name and add data attributes
        if (isMiniGameLink) {
          let gameName = '';
          try {
            const url = new URL(href, window.location.origin);
            gameName = url.searchParams.get('name') || text.replace(/<[^>]*>/g, '').replace(/[\p{Emoji}]/gu, '').trim();
          } catch (_e) {
            gameName = text.replace(/<[^>]*>/g, '').replace(/[\p{Emoji}]/gu, '').trim();
          }
          return match.replace('href="' + href + '"', `href="${updatedHref}" data-game-name="${gameName}" data-game-link="true"`);
        }
      }

      // Return modified link with updated href
      return match.replace('href="' + href + '"', `href="${updatedHref}"`);
    })
    .replace(/<br\s*\/?>/gi, '<br />')
    .replace(/(<\/ul>)/gi, '$1<div class="ai-spacing"></div>')
    .replace(/<table>/gi, '<table class="ai-table">')
    .replace(/<th>/gi, '<th class="ai-th">')
    .replace(/<td>/gi, '<td class="ai-td">');
  return DOMPurify.sanitize(f, {
    ALLOWED_TAGS: ['h1','h2','h3','h4','h5','h6','p','strong','em','ul','ol','li','br','div','span','table','thead','tbody','tr','th','td','a'],
    ALLOWED_ATTR: ['class','href','data-game-name','data-game-link','isMiniGame','onclick','title','style','target'],
  });
};

// ── Game Emoji Lookup ───────────────────────────────────────────────────────
const getGameEmoji = (gameName) => {
  const n = (gameName || '').toLowerCase();
  if (n.includes('aviator')) return '✈️';
  if (n.includes('rocket') || n.includes('crash') || n.includes('aero')) return '🚀';
  if (n.includes('defender')) return '🛡️';
  if (n.includes('spinny') || n.includes('wheel') || n.includes('roulette')) return '🎡';
  if (n.includes('baccarat')) return '🃏';
  if (n.includes('poker') || n.includes('slot')) return '🎰';
  if (n.includes('dice')) return '🎲';
  return '🎮';
};

// ─────────────────────────────────────────────────────────────────────────────
// LOYALTY TIER CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const loyaltyTierLabels = { level0:'DASH', level1:'SPRINT', level2:'SURGE', level3:'THRILL', level4:'BOSS' };
const formatLoyaltyTierLabel = (t) => loyaltyTierLabels[(t || '').toLowerCase()] || t || 'Unknown';
const LOYALTY_TIER_IMAGES = {
  level0: require('../../../assets/images/loyalty/dash.webp'),
  level1: require('../../../assets/images/loyalty/sprint.webp'),
  level2: require('../../../assets/images/loyalty/surge.webp'),
  level3: require('../../../assets/images/loyalty/thrill.webp'),
  level4: require('../../../assets/images/loyalty/boss.webp'),
};

// ─────────────────────────────────────────────────────────────────────────────
// API DATA RENDERERS
// ─────────────────────────────────────────────────────────────────────────────

// ── Balance ──────────────────────────────────────────────────────────────────
const renderBalanceData = (data, key) => (
  <div key={key} className="balance-data-display rounded-lg overflow-hidden shadow-md">
    <div className="balance-header flex items-center gap-2 p-4">
      <h3 className="balance-title text-lg font-bold">Account Balance</h3>
    </div>
    <div className="balance-items flex flex-col gap-3 p-4">
      <div className="balance-item main-balance flex justify-between items-center p-3 rounded-md">
        <span className="balance-label">Wallet Balance</span>
        <span className="balance-value font-bold text-lg">
          <span className="currency">{data.currency}</span> {data.wallet_balance?.toLocaleString() || '0'}
        </span>
      </div>
      {data.bonus_amount !== undefined && (
        <div className="balance-item flex justify-between items-center p-3 rounded-md">
          <span className="balance-label">Bonus Amount</span>
          <span className="balance-value font-bold">
            <span className="currency">{data.currency}</span> {data.bonus_amount?.toLocaleString() || '0'}
          </span>
        </div>
      )}
      {data.coins_balance !== undefined && (
        <div className="balance-item flex justify-between items-center p-3 rounded-md">
          <span className="balance-label">Coins Balance</span>
          <span className="balance-value coins font-bold">{(data.coins_balance || 0).toLocaleString()}</span>
        </div>
      )}
    </div>
  </div>
);

// ── Account Overview ─────────────────────────────────────────────────────────
const renderUserStateData = (data, key, navigate = null, dispatch = null) => {
  const tierKey = (data.loyalty_tier_name || '').toLowerCase() || 'level0';
  const tierLabel = formatLoyaltyTierLabel(data.loyalty_tier_name);
  const tierImg = LOYALTY_TIER_IMAGES[tierKey] || LOYALTY_TIER_IMAGES.level0;
  return (
    <div key={key} className="user-state-display">
      <div className="user-state-header">
        <UserCheck size={18} className="user-state-icon" />
        <div className="user-state-title">Account Overview</div>
      </div>
      <div className="user-state-content">
        <div className="user-info-grid d-none">
          <div className="info-cell"><span className="cell-label">User ID</span><span className="cell-value">{data.user_id}</span></div>
          <div className="info-cell">
            <span className="cell-label">Status</span>
            <span className={`cell-value status-indicator ${data.login_status ? 'online' : 'offline'}`}>
              <span className="dot">●</span> {data.login_status ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        <div className={`loyalty-tier-card tier-${tierKey}`}>
          <div className="loyalty-tier-icon-wrap"><img src={tierImg} alt={tierLabel} className="loyalty-tier-img" /></div>
          <div className="loyalty-tier-info">
            <span className="loyalty-tier-label-text">Loyalty Level</span>
            {/* <span className="loyalty-tier-name">{tierLabel}</span> */}
              <span className="loyalty-tier-name">{data.loyalty_tier_name}</span>
          </div>
        </div>
      </div>
      <div className="user-state-footer">
        <button
          type="button"
          className="user-state-view-more"
          onClick={() => {
            if (dispatch) dispatch(setIsActive(false)); // Close chat
            if (navigate) navigate('/loyalty');         // Navigate to Loyalty
          }}
          style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer' }}
        >
          View More <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
};

// ── Game Search Results ───────────────────────────────────────────────────────
const renderSearchGamesData = (data, key, dispatch = null, navigate = null, customTitle = null) => {
  const handlePlayGame = (game) => {
    if (!navigate) return;
    let parsedJson = null;
    try { parsedJson = game.gameJsonData ? JSON.parse(game.gameJsonData) : null; } catch (_e) {}

    const gameData = {
      aggregator_type: (game.aggregatorType || parsedJson?.aggregator).toLowerCase(),
      provider:        game.provider || parsedJson?.provider,
      name:            game.game_name || parsedJson?.gameName,
      game_code:       parsedJson?.gameId || game.gameId,
      game_id:         parsedJson?.gameId || game.gameId,
      game_type:       game.game_type || parsedJson?.gameCategory,
      image:           { url: game.imageUrl || parsedJson?.imageUrl },
      categoryname:    game.game_type || parsedJson?.gameCategory,
      categoryid:      game.categoryId,
      demoSupport:     parsedJson?.demoSupport,
      freeBet:         game.freeBet,
    };
    const url = buildGameLaunchUrl(gameData, { gameClose: true, returnPath: window.location.pathname });
    window.location.href = url;
  };

  return (
    <div key={key} className="search-games-display">
      <div className="search-games-header">
        <Search size={15} className="search-header-icon" />
        <div className="search-title">
          {customTitle
            ? `${customTitle} (${data?.length || 0})`
            : `Games Found (${data?.length || 0})`
          }
        </div>
      </div>
      {data && data.length > 0 ? (
        <Splide
          options={{ type: 'slide', perPage: 2, perMove: 1, gap: '10px', arrows: true, pagination: false, drag: true, breakpoints: { 400: { perPage: 1 } } }}
          className="search-games-carousel"
        >
          {data.map((game, idx) => {
            const thumb = game.imageUrl || null;
            const provider = game.provider || null;
            const gameName = game.game_name || game.gameName || '—';
            const gameType = game.game_type || game.gameType || null;
            return (
              <SplideSlide key={idx}>
                <div className="sgc-card">
                  <div className="sgc-thumb-wrap">
                    {thumb
                      ? <img src={thumb} alt={gameName} className="sgc-thumb" onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                      : null}
                    <div className="sgc-thumb-placeholder" style={{ display: thumb ? 'none' : 'flex' }}>
                      <span className="sgc-emoji">{getGameEmoji(gameName)}</span>
                    </div>
                    <div className="sgc-thumb-overlay" />
                    <div className="sgc-thumb-top">
                      {game.is_new && <span className="sgc-badge new"><Sparkles size={9} />New</span>}
                      {game.bonus_eligible && <span className="sgc-badge bonus"><Gift size={9} />Bonus</span>}
                      {game.is_trending && <span className="sgc-badge trending"><Flame size={9} />Hot</span>}
                    </div>
                    {gameType && <span className="sgc-type-pill">{gameType}</span>}
                  </div>
                  <div className="sgc-body">
                    <div className="sgc-name" title={gameName}>{gameName}</div>
                    {provider && <div className="sgc-provider">{provider}</div>}
                    {game.min_bet > 0 && <div className="sgc-minbet">Min bet: <strong>KES {Number(game.min_bet).toLocaleString()}</strong></div>}
                    <button type="button" className="sgc-play-btn" onClick={() => handlePlayGame(game)}>
                      <Play size={12} fill="currentColor" /> Play Now
                    </button>
                  </div>
                </div>
              </SplideSlide>
            );
          })}
        </Splide>
      ) : (
        <div className="data-empty-state">
          <Gamepad2 size={28} className="empty-icon" />
          <span className="empty-title">No games found</span>
          <span className="empty-subtitle">Try a different game name or category.</span>
        </div>
      )}
    </div>
  );
};

// ── Gameplay / Game History ───────────────────────────────────────────────────
const formatPlayDate = (raw) => {
  if (!raw) return null;
  try {
    let d;
    if (Array.isArray(raw) && raw.length >= 5) {
      // API returns [year, month, day, hour, minute, second] — month is 1-based
      d = new Date(raw[0], raw[1] - 1, raw[2], raw[3], raw[4], raw[5] || 0);
    } else if (typeof raw === 'number') {
      d = new Date(raw < 1e12 ? raw * 1000 : raw);
    } else {
      d = new Date(raw);
    }
    if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_e) { return String(raw); }
};

const gameResultMeta = (result) => {
  const r = (result || '').toLowerCase();
  if (r === 'won' || r === 'win') return { label: 'Won', cls: 'won' };
  if (r === 'lost' || r === 'lose') return { label: 'Lost', cls: 'lost' };
  return { label: result || '—', cls: 'draw' };
};

const GameHistoryCard = ({ game }) => {
  const thumb = game.thumbnail || game.image || null;
  const provider = game.providerName || game.provider || null;
  const stake = game.stake ?? game.stakeAmount ?? game.bet_amount ?? null;
  const payout = game.finalPayout ?? game.payout ?? game.payoutAmount ?? game.win_amount ?? null;
  const playDate = formatPlayDate(game.createdDate || game.playedAt || game.played_at || game.createdAt || game.date || null);
  const resultRaw = game.gameResult || game.betStatus || '';
  const { label: resultLabel, cls: resultCls } = gameResultMeta(resultRaw);
  return (
    <div className="ghc-card">
      <div className="ghc-thumb">
        {thumb
          ? <img src={thumb} alt={game.gameName} className="ghc-thumb-img" />
          : <div className="ghc-thumb-placeholder"><span className="ghc-emoji">{getGameEmoji(game.gameName)}</span></div>}
        <span className={`ghc-result-badge ${resultCls}`}>{resultLabel}</span>
      </div>
      <div className="ghc-details">
        <div className="ghc-name">{game.gameName || '—'}</div>
        <div className="ghc-meta-row">
          {provider && <span className="ghc-provider">{provider}</span>}
          {game.gameType && <span className="ghc-type">{game.gameType}</span>}
        </div>
        <div className="ghc-finance-row">
          <div className="ghc-finance-item">
            <span className="ghc-finance-label">Stake</span>
            <span className="ghc-finance-value">{stake !== null ? `KES ${Number(stake).toLocaleString()}` : '—'}</span>
          </div>
          <div className="ghc-finance-item">
            <span className="ghc-finance-label">Payout</span>
            <span className={`ghc-finance-value ${payout !== null && Number(payout) > 0 ? 'positive' : ''}`}>
              {payout !== null ? `KES ${Number(payout).toLocaleString()}` : '—'}
            </span>
          </div>
        </div>

        {playDate && (
          <div className="ghc-date">
            <Clock size={10} />
            {playDate}
          </div>
        )}
      </div>
    </div>
  );
};

const renderGameplayData = (data, key) => {
  const lastGame = data.lastGame ? {
    gameName: data.lastGame.gameName || data.lastGame.gameCode,
    gameType: data.lastGame.gameType || data.lastGame.category,
    gameResult: data.lastGame.gameResult || data.lastGame.betStatus,
    stake: data.lastGame.stake,
    finalPayout: data.lastGame.finalPayout,
    createdDate: data.lastGame.createdDate,
    providerName: data.lastGame.providerName,
  } : null;
  const recentGames = data.recentGames || [];
  return (
    <div key={key} className="gameplay-data-display">
      <div className="gh-header"><Clock size={15} className="gh-header-icon" /><h3 className="gh-title">Game History</h3></div>
      <div className="gh-stats-grid">
        <div className="gh-stat"><span className="gh-stat-value won">{(data.wins || 0).toLocaleString()}</span><span className="gh-stat-label">Total Wins</span></div>
        <div className="gh-stat"><span className="gh-stat-value lost">{(data.losses || 0).toLocaleString()}</span><span className="gh-stat-label">Total Losses</span></div>
        {data.todayCounts && (
          <>
            <div className="gh-stat"><span className="gh-stat-value">{(data.todayCounts.played || 0).toLocaleString()}</span><span className="gh-stat-label">Today Played</span></div>
            <div className="gh-stat"><span className="gh-stat-value won">{(data.todayCounts.won || 0).toLocaleString()}</span><span className="gh-stat-label">Today Won</span></div>
          </>
        )}
      </div>
      {recentGames.length > 0 ? (
        <div className="gh-recent">
          <div className="gh-section-label">Recent Games ({recentGames.length})</div>
          <div className="gh-cards-list">{recentGames.map((g, i) => <GameHistoryCard key={i} game={g} />)}</div>
          {/* {recentGames.length > 8 && <div className="gh-more-note">Showing 8 of {recentGames.length} games</div>} */}
        </div>
      ) : (
        <div className="data-empty-state">
          <Clock size={28} className="empty-icon" />
          <span className="empty-title">No game history yet</span>
        </div>
      )}
      {lastGame && <div className="gh-last"><div className="gh-section-label">Last Game Played</div><GameHistoryCard game={lastGame} /></div>}
    </div>
  );
};

const IframeMessage = ({ url, title, initialCollapsed = false, onClose, dispatch, token, isLoggedIn }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const [iframeKey, setIframeKey] = React.useState(0);
  const [isCollapsed, setIsCollapsed] = React.useState(initialCollapsed);

  React.useEffect(() => {
    console.log('[IFRAME] IframeMessage rendered:', { title, initialCollapsed, currentCollapsed: isCollapsed, url });
  }, [title, initialCollapsed, isCollapsed, url]);

  const handleCollapse = () => {
    console.log('[IFRAME] User collapsed:', title);
    setIsCollapsed(true);
    // Track that user manually closed this game
    try {
      const userClosedKey = `chat_game_user_closed_${url}`;
      localStorage.setItem(userClosedKey, '1');
    } catch (_e) { /* ignore */ }
    onClose?.();
  };

  const handleExpand = () => {
    console.log('[IFRAME] User expanded:', title);
    setIsCollapsed(false);
    setIsLoading(true);
    setHasError(false);
    setIframeKey(k => k + 1);
  };

  // Refresh balance while game is open to show win/loss in real-time
  React.useEffect(() => {
    if (isCollapsed || !isLoggedIn || !token || !dispatch) return;

    // Fetch balance from server every 2 seconds while game is open
    const interval = setInterval(() => {
      dispatch(fetchAccountDetails(token));
    }, 2000);

    return () => clearInterval(interval);
  }, [isCollapsed, token, dispatch, isLoggedIn]);

  if (isCollapsed) {
    return (
      <div className="collapsed-iframe-section" onClick={handleExpand}>
        <div className="collapsed-content">
          <span>{getGameEmoji(title)}</span>
          <span>{title || 'Game'}</span>
          <Maximize2 size={16} />
        </div>
      </div>
    );
  }

  return (
    <div className="message-iframe-container">
      <div className="iframe-controls-bar">
        <span className="iframe-game-label">{getGameEmoji(title)} {title || 'Game'}</span>
        <button type="button" className="btn-close-iframe" onClick={handleCollapse} title="Collapse game">
          <X size={16} />
        </button>
      </div>
      {isLoading && !hasError && (
        <div className="iframe-loading-placeholder">
          <div className="iframe-loader"><div className="loader-spinner" /><span>Loading {title || 'game'}…</span></div>
        </div>
      )}
      {hasError && (
        <div className="iframe-error-placeholder">
          <div className="error-content">
            <span className="error-icon">⚠️</span>
            <p>Failed to load game</p>
            <button type="button" className="btn-retry-iframe" onClick={() => { setHasError(false); setIsLoading(true); setIframeKey(k => k + 1); }}>Retry</button>
          </div>
        </div>
      )}
      {!hasError && (
        <iframe
          key={iframeKey} src={url} title={title || 'Game'}
          className={`message-iframe${isLoading ? ' loading' : ''}`}
          onLoad={() => setIsLoading(false)}
          onError={() => { setIsLoading(false); setHasError(true); }}
          allow="fullscreen"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      )}
    </div>
  );
};

// Container for multiple games when user launches multiple at once
const MultiGameContainer = ({ games, token, userId, navigate, dispatch, messageId }) => {
  if (!games || games.length === 0) return null;

  return (
    <div className="multi-game-container">
      <div className="multi-game-grid">
        {games.map((gameData, idx) => (
          <div key={idx} className="multi-game-item">
            <PlayGameMessage
              data={gameData}
              token={token}
              userId={userId}
              navigate={navigate}
              dispatch={dispatch}
              messageId={messageId}
              isMiniGame={gameData.hasMiniGame === true}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const PlayGameMessage = ({ data, token, userId, navigate, dispatch, messageId }) => {
  let game = data.game;

  // If game data is missing, try to restore from localStorage
  if (!game || !game.gameId) {
    try {
      const savedGameData = localStorage.getItem(`fasfas_chat_game_data_${data?.game?.gameName || 'unknown'}`);
      if (savedGameData) {
        game = JSON.parse(savedGameData);
        console.log('[GAME] Restored game data from localStorage:', game?.gameName);
      }
    } catch (_e) {
      console.warn('[GAME] Failed to restore game data from localStorage');
    }
  }

  // Auto-launch games by default, regardless of hasMiniGame flag from backend
  // This ensures consistent auto-launch behavior for all games
  const hasMiniGame = data.hasMiniGame !== false; // True if hasMiniGame is true or undefined

  const [iframeSrc, setIframeSrc] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [launchError, setLaunchError] = React.useState(null);
  const launchAttemptedRef = React.useRef(false); // Prevent duplicate launch attempts

  // Use messageId in the key so each new chat request gets its own tracking
  // This allows the same game to auto-launch again when requested in a new message
  const gameSessionKey = `chat_game_seen_${messageId}_${game?.gameId || game?.gameName || 'unknown'}`;
  const gameDataKey = `fasfas_chat_game_data_${game?.gameId || game?.gameName || 'unknown'}`;

  // Check if this specific message's game was already launched
  // For auto-launched games (hasMiniGame=true), start expanded ONLY on first launch
  // On page reload or navigation back, start collapsed to avoid auto-expanding old games
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialCollapsed = React.useMemo(() => {
    try {
      if (!game) return false;

      const wasThisMessageGameSeenBefore = sessionStorage.getItem(gameSessionKey) === '1';

      if (hasMiniGame) {
        // For auto-launched games: expand on FIRST request, collapse on subsequent renders
        // This ensures auto-launch only happens when NEW message arrives, not on page reload
        const shouldBeCollapsed = wasThisMessageGameSeenBefore;
        console.log('[GAME] Auto-launch game init:', {
          gameName: game?.gameName,
          messageId,
          gameSessionKey,
          sessionStorageFlag: wasThisMessageGameSeenBefore ? '✅ FOUND' : '❌ NOT FOUND',
          isFirstRequest: !wasThisMessageGameSeenBefore,
          initialCollapsed: shouldBeCollapsed
        });
        return shouldBeCollapsed;
      }

      // Non-auto-launched games: collapse if already seen
      console.log('[GAME] Non-auto-launch game init:', { gameName: game?.gameName, messageId, wasSeenBefore: wasThisMessageGameSeenBefore });
      return wasThisMessageGameSeenBefore;
    }
    catch (_e) { return false; }
  }, [gameSessionKey, hasMiniGame, game?.gameName, game, messageId]);

  const fetchLaunchUrl = React.useCallback(async () => {
    setLaunchError(null);
    try {
      // Validate game data before attempting launch
      if (!game || (!game.gameId && !game.gameName)) {
        console.error('[GAME] ❌ Invalid game data - missing gameId and gameName:', game);
        setLaunchError('Game data is invalid. Please try again.');
        setIsLoading(false);
        return;
      }

      let parsedJson = null;
      try { parsedJson = game.gameJsonData ? JSON.parse(game.gameJsonData) : null; } catch (_e) { parsedJson = null; }

      const resolvedGameId = parsedJson?.gameId || game.gameId || game.gameName;
      const resolvedAggregator = (parsedJson?.aggregator || game.aggregatorType || 'negames').toLowerCase();
      const resolvedProvider = parsedJson?.provider || game.provider || 'negames';

      if (!resolvedGameId) {
        console.error('[GAME] ❌ Invalid game ID after resolution:', { resolvedGameId, game });
        setLaunchError('Could not determine game ID. Please try again.');
        setIsLoading(false);
        return;
      }

      let playerId = String(userId || '0');
      if (playerId === '0') {
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith('fasfas_chat_history_') && !k.includes('guest')) {
              const id = k.replace('fasfas_chat_history_', '');
              if (id && !isNaN(Number(id))) { playerId = id; break; }
            }
          }
        } catch (_e) { /* ignore */ }
      }

      console.log('[CHAT] launchGame — playerId:', playerId, '| gameId:', resolvedGameId);

      const response = await axios.post(LAUNCH_GAME_ENDPOINT, {
        playerId,
        platformId: 1,
        operatorId: 'fasfas',
        brandId: 'fasfas',
        playerToken: token,
        currencyCode: 'KES',
        gameId: resolvedGameId,
        aggregator: resolvedAggregator,
        provider: resolvedProvider,
        technology: 'H5',
        gamePlatform: 'WEB',
        playerIp: '127.0.0.1',
        timestamp: Date.now(),
        countryCode: 'KE',
        lobbyUrl: 'https://fasfas-qa.negroup-tech.net/',
        depositUrl: 'https://fasfas-qa.negroup-tech.net/deposit',
      });

      const launchUrl = response?.data?.launchUrl || response?.data?.url;
      console.log('[GAME] Launch response received:', { launchUrl, responseData: response?.data });

      if (launchUrl) {
        console.log('[GAME] ✅ Setting iframe source:', launchUrl);
        setIframeSrc(launchUrl);
        try {
          // Mark this message+game as "seen" so it doesn't auto-launch on reload
          sessionStorage.setItem(gameSessionKey, '1');
          console.log('[GAME] ✅ Marked as seen (won\'t auto-launch on reload):', gameSessionKey);
          // Persist game data to localStorage for page reload/tab switch recovery
          localStorage.setItem(gameDataKey, JSON.stringify(game));
          // Auto-cleanup: Remove game data after 30 minutes if game is not being played
          setTimeout(() => {
            try { localStorage.removeItem(gameDataKey); } catch (_e) { /* ignore */ }
          }, 30 * 60 * 1000); // 30 minutes
        } catch (_e) { /* ignore */ }
      } else {
        const reason = response?.data?.token || response?.data?.message || 'Unknown error';
        console.log('[GAME] ❌ No launch URL in response:', reason);
        setLaunchError(`Could not launch game: ${reason}`);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err.message || 'Failed to launch game.';
      console.error('[GAME] ❌ Launch error:', errorMsg, err);
      setLaunchError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [token, userId, game]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset launch guard when game changes (allows re-launching same game)
  React.useEffect(() => {
    if (game?.gameId) {
      launchAttemptedRef.current = false;
      console.log('[GAME] Reset launch guard for:', game?.gameName);
    }
  }, [game?.gameId]); // Reset when game ID changes

  React.useEffect(() => {
    // Debug: log conditions
    console.log('[GAME] Launch conditions:', {
      hasMiniGame,
      gameAvailable: !!game,
      gameName: game?.gameName,
      notAttempted: !launchAttemptedRef.current,
      shouldLaunch: hasMiniGame && game && !launchAttemptedRef.current
    });

    // Auto-launch game when hasMiniGame is true AND game data is available
    if (hasMiniGame && game && !launchAttemptedRef.current) {
      launchAttemptedRef.current = true;
      setIsLoading(true);
      console.log('[GAME] ✅ Auto-launching:', game.gameName);
      fetchLaunchUrl();
    } else if (hasMiniGame && !game) {
      console.log('[GAME] ⚠️ hasMiniGame=true but NO game data available');
    }
  }, [hasMiniGame, game, fetchLaunchUrl]); // Proper dependencies for auto-launch

  // Note: Do NOT clear game data on unmount - let it persist for page reloads
  // Game data will be cleared after game successfully completes or after timeout
  // This allows users to reload the page without losing game context

  if (!game) {
    console.warn('[GAME] ⚠️ No game data available:', { messageId, gameSessionKey });
    return (
      <div className="chat-message-item error-message-normal">
        <span className="error-icon-small">⚠️</span>
        <span className="error-text-small">Game data unavailable. Please request the game again.</span>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="chat-message-item error-message-normal">
        <span className="error-icon-small">⚠️</span>
        <span className="error-text-small">{data.error}</span>
      </div>
    );
  }

  if (hasMiniGame) {
    if (isLoading) {
      return (
        <div className="play-game-display">
          <div className="play-game-loader-state">
            <div className="typing-indicator"><span></span><span></span><span></span></div>
            <span className="play-game-loading-label">Loading {game.gameName || 'game'}…</span>
          </div>
        </div>
      );
    }
    if (launchError) {
      const handleRetry = () => {
        launchAttemptedRef.current = false; // Reset launch guard to allow retry
        setLaunchError(null);
        setIsLoading(true);
        fetchLaunchUrl();
      };

      return (
        <div className="chat-message-item error-message-normal">
          <span className="error-icon-small">⚠️</span>
          <span className="error-text-small">{launchError}</span>
          <button className="error-retry-link" onClick={handleRetry}>Retry</button>
        </div>
      );
    }
    if (iframeSrc) {
      console.log('[GAME] Rendering IframeMessage:', { gameName: game.gameName, initialCollapsed, iframeSrc });
      return <IframeMessage url={iframeSrc} title={game.gameName} initialCollapsed={initialCollapsed} dispatch={dispatch} token={token} isLoggedIn={token !== null} />;
    }
    console.log('[GAME] No iframeSrc yet, waiting for launch URL:', { gameName: game.gameName, hasMiniGame });
    return null;
  }

  // hasMiniGame=false: navigate to game launch page using chat API data + gameJsonData
  const handleNavigateToGame = () => {
    let parsedJson = null;
    try { parsedJson = game.gameJsonData ? JSON.parse(game.gameJsonData) : null; } catch (_e) {}

    const gameData = {
      aggregator_type: (game.aggregatorType || parsedJson?.aggregator).toLowerCase(),
      provider:        game.provider || parsedJson?.provider,
      name:            game.gameName || parsedJson?.gameName,
      game_code:       parsedJson?.gameId || game.gameId,
      game_id:         parsedJson?.gameId || game.gameId,
      game_type:       game.gameType || parsedJson?.gameCategory,
      image:           { url: game.imageUrl || parsedJson?.imageUrl },
      categoryname:    game.category || parsedJson?.gameCategory,
      categoryid:      game.categoryId,
      demoSupport:     parsedJson?.demoSupport,
      freeBet:         game.freeBet,
    };
    const url = buildGameLaunchUrl(gameData, { gameClose: true, returnPath: window.location.pathname });
    window.location.href = url;
  };

  return (
    <div className="play-game-display">
      <div className="sgc-card single-play-card">
        <div className="sgc-thumb-wrap">
          {(game.imageUrl || game.image) ? (
            <img
              src={game.imageUrl || game.image}
              alt={game.gameName}
              className="sgc-thumb"
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div className="sgc-thumb-placeholder" style={{ display: (game.imageUrl || game.image) ? 'none' : 'flex' }}>
            <span className="sgc-emoji">{getGameEmoji(game.gameName)}</span>
          </div>
          <div className="sgc-thumb-overlay" />
          <div className="sgc-thumb-top">
            {game.is_new && <span className="sgc-badge new"><Sparkles size={9} />New</span>}
            {game.bonus_eligible && <span className="sgc-badge bonus"><Gift size={9} />Bonus</span>}
            {game.is_trending && <span className="sgc-badge trending"><Flame size={9} />Hot</span>}
          </div>
          {(game.gameType || game.category) && (
            <span className="sgc-type-pill">{game.gameType || game.category}</span>
          )}
        </div>
        <div className="sgc-body">
          <div className="sgc-name" title={game.gameName}>{game.gameName}</div>
          {game.provider && <div className="sgc-provider">{game.provider}</div>}
          {game.min_bet > 0 && (
            <div className="sgc-minbet">Min bet: <strong>KES {Number(game.min_bet).toLocaleString()}</strong></div>
          )}
          <button type="button" className="sgc-play-btn" onClick={handleNavigateToGame}>
            <Play size={12} fill="currentColor" /> Ready to Play
          </button>
        </div>
      </div>
    </div>
  );
};

const renderPlayGameData = (data, key, token, userId, navigate, dispatch, messageId) => {
  return <PlayGameMessage key={key} data={data} token={token} userId={userId} navigate={navigate} dispatch={dispatch} messageId={messageId} />;
};

const renderApiDataMessage = (messageData, dispatch, navigate, token, userId, messageId) => {
  if (!messageData || !Array.isArray(messageData)) return null;

  // Check if there are multiple play_game items - if so, group them
  const playGameItems = messageData.filter(item => item.messageType === 'play_game');
  const isMultipleGames = playGameItems.length > 1;

  const rendered = [];
  let gamesGroupAdded = false;

  messageData.forEach((item, index) => {
    const k = item.id || `${item.messageType}_${index}`;

    // Group multiple games together
    if (item.messageType === 'play_game') {
      if (isMultipleGames && !gamesGroupAdded) {
        // Add all games at once in a container
        rendered.push(
          <MultiGameContainer
            key={`multi_games_${index}`}
            games={playGameItems.map(g => g.data)}
            token={token}
            userId={userId}
            navigate={navigate}
            dispatch={dispatch}
            messageId={messageId}
          />
        );
        gamesGroupAdded = true;
      } else if (!isMultipleGames) {
        // Single game - render normally
        rendered.push(renderPlayGameData(item.data, k, token, userId, navigate, dispatch, messageId));
      }
    } else {
      // Handle other message types normally
      switch (item.messageType) {
        case 'get_balances':
          rendered.push(renderBalanceData(item.data, k));
          break;
        case 'get_gameplay':
          rendered.push(renderGameplayData(item.data, k));
          break;
        case 'get_user_state':
          rendered.push(renderUserStateData(item.data, k, navigate, dispatch));
          break;
        case 'search_games':
          rendered.push(renderSearchGamesData(item.data, k, dispatch, navigate));
          break;
        case 'get_games_by_category': {
          let games = [];
          if (item.data && Array.isArray(item.data.games)) {
            games = item.data.games;
          } else if (Array.isArray(item.data)) {
            games = item.data;
          } else if (Array.isArray(item.games)) {
            games = item.games;
          }
          let categoryName = "Category";
          if (item.data?.categories && Array.isArray(item.data.categories) && item.data.categories.length > 0) {
            categoryName = item.data.categories[0];
          } else if (item.data?.categoryName) {
            categoryName = item.data.categoryName;
          } else if (item.data?.category) {
            categoryName = item.data.category;
          } else if (item.categoryName) {
            categoryName = item.categoryName;
          } else if (item.category) {
            categoryName = item.category;
          }
          rendered.push(renderSearchGamesData(games, k, dispatch, navigate, `${categoryName} Games Found`));
          break;
        }
        case 'get_games_by_provider': {
          let games = [];
          if (item.data && Array.isArray(item.data.games)) {
            games = item.data.games;
          } else if (Array.isArray(item.data)) {
            games = item.data;
          } else if (Array.isArray(item.games)) {
            games = item.games;
          }
          rendered.push(renderSearchGamesData(games, k, dispatch, navigate, "Provider Games Found"));
          break;
        }
        case 'list_providers':
          rendered.push(renderProvidersList(item.data, k));
          break;
        case 'list_categories':
          rendered.push(renderCategoriesList(item.data, k));
          break;
        case 'data_and_airesponse':
          rendered.push(
            <div key={k} className="api-combined-message">
              {item.reply ? (
                <div className="ai-response-display">
                  <div className="ai-response-content" dangerouslySetInnerHTML={{ __html: formatAIResponseContent(item.reply) }} />
                </div>
              ) : null}
              {Array.isArray(item.data) ? renderApiDataMessage(item.data, dispatch, navigate, token, userId, messageId) : null}
            </div>
          );
          break;
        case 'deposit_withdraw_history':
          rendered.push(renderDepositWithdrawHistory(item.data, k));
          break;
        default:
          break;
      }
    }
  });

  return rendered;
};

const WELCOME_ITEMS_BEFORE_LOGIN = [
  { icon: UserCheck,   shortLabel: 'Your Account',       actionText: 'Account registration and eligibility help', text: 'Account registration & eligibility help' },
  { icon: Coins,       shortLabel: 'FasFas Coins',       actionText: 'FasFas Coins and loyalty program details',  text: 'Loyalty program & FasFas Coins details' },
  { icon: CreditCard,  shortLabel: 'Deposits & Withdrawals', actionText: 'Help with deposits and withdrawals',        text: 'Deposits, withdrawals & transaction support' },
  { icon: RefreshCw,   shortLabel: 'Forgot Pin',         actionText: 'Help me forgot my Pin',                 text: 'Forgot password' },
];

const WELCOME_ITEMS_AFTER_LOGIN = [
  { icon: Wallet,      shortLabel: 'Balance',            actionText: 'Check my account balance',                  text: 'Check your account balance' },
  { icon: Gamepad2,    shortLabel: 'Game History',       actionText: 'Show my recent games and game history',     text: 'View recent games & game history' },
  { icon: Gamepad2,    shortLabel: 'Mini Games',         actionText: 'Show mini games',                           text: 'Show Mini Games' },
  { icon: Trophy,      shortLabel: 'Loyalty',            actionText: 'Show my loyalty status',                    text: 'View your loyalty status & tier progress' },
  { icon: UserCheck,   shortLabel: 'Account Help',       actionText: 'Account registration and eligibility help', text: 'Account registration & eligibility help' },
  { icon: Gift,        shortLabel: 'Bonuses',            actionText: 'Show bonuses, promotions and rewards',      text: 'Bonuses, promotions & rewards information' },
  { icon: Coins,       shortLabel: 'FasFas Coins',       actionText: 'FasFas Coins and loyalty program details',  text: 'Loyalty program & FasFas Coins details' },
  { icon: CreditCard,  shortLabel: 'Deposits & Withdrawals', actionText: 'Help with deposits and withdrawals',        text: 'Deposits, withdrawals & transaction support' },
  { icon: RefreshCw,   shortLabel: 'Reset Password',     actionText: 'Help me reset my password',                 text: 'Reset my password' },
  // { icon: ShieldCheck, shortLabel: 'Set Limits',         actionText: 'Set limits to my gameplay',                 text: 'Set Limits to My Gameplay' },
  { icon: Sparkles,    shortLabel: 'Suggest Games',      actionText: 'Suggest games to play',                     text: 'Suggest Games to Play' },
  { icon: CreditCard,  shortLabel: 'Deposit History',    actionText: 'Show my deposit history',                   text: 'Show Deposit History' },
  { icon: Coins,       shortLabel: 'Withdraw History',   actionText: 'Show my withdraw history',                  text: 'Show Withdraw History' },
];

const WelcomeMessage = ({ isLoggedIn = false }) => {
  const items = isLoggedIn ? WELCOME_ITEMS_AFTER_LOGIN : WELCOME_ITEMS_BEFORE_LOGIN;
  return (
    <div className="welcome-message">
      <p className="welcome-greeting">Hi there! 👋</p>
      <p className="welcome-subtext">How can I assist you today?</p>
      <ul className="welcome-list">
        {items.map(({ icon: Icon, text }, idx) => (
          <li key={idx} className="welcome-list-item"><Icon size={13} className="welcome-icon" /><span>{text}</span></li>
        ))}
      </ul>
      <p className="welcome-footer">Let me know what you'd like to do!</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ERROR MESSAGE UI
// ─────────────────────────────────────────────────────────────────────────────
const ERROR_META = {
  500: {
    headline: 'Server Error',
    description: "Our server hit a snag processing your request. This is temporary — please try again in a moment.",
    emoji: '🛠️',
  },
  network: {
    headline: 'No Connection',
    description: 'Unable to reach the server. Please check your internet connection and try again.',
    emoji: '📡',
  },
  timeout: {
    headline: 'Request Timed Out',
    description: 'The server took too long to respond. Please try again.',
    emoji: '⏱️',
  },
  auth: {
    headline: 'Session Expired',
    description: 'Your session has expired. Please refresh the page and log in again.',
    emoji: '🔒',
  },
  generic: {
    headline: 'Something Went Wrong',
    description: 'An unexpected error occurred. Please try again.',
    emoji: '⚠️',
  },
};

const ErrorMessage = ({ content, onRetry }) => {
  const [retrying, setRetrying] = React.useState(false);
  const meta = ERROR_META[content?.code] || ERROR_META.generic;
  const canRetry = content?.retryMessage && onRetry && content?.code !== 'auth';

  const handleRetry = async () => {
    if (!canRetry || retrying) return;
    setRetrying(true);
    try { await onRetry(content.retryMessage); } finally { setRetrying(false); }
  };

  return (
    <div className="chat-error-message">
      <div className="cem-icon-wrap">
        <span className="cem-emoji">{meta.emoji}</span>
        <AlertTriangle size={14} className="cem-alert-icon" />
      </div>
      <div className="cem-body">
        <span className="cem-headline">{meta.headline}</span>
        <span className="cem-description">{content?.description || meta.description}</span>
      </div>
      {canRetry && (
        <button
          type="button"
          className={`cem-retry-btn${retrying ? ' retrying' : ''}`}
          onClick={handleRetry}
          disabled={retrying}
          title="Retry"
        >
          <RefreshCw size={13} className={retrying ? 'spinning' : ''} />
          <span>{retrying ? 'Retrying…' : 'Try again'}</span>
        </button>
      )}
    </div>
  );
};

const LoggedOutBanner = () => (
  <div className="logged-out-banner">
    <div className="logged-out-icon-wrap"><UserCheck size={32} className="logged-out-icon" /></div>
    <h4 className="logged-out-title">You've been logged out</h4>
    <p className="logged-out-subtitle">Your session has expired or you logged in elsewhere. Please log in again to continue using chat and account features.</p>
    <div className="logged-out-actions">
      <a
        href="/signin"
        className="logged-out-btn primary"
        onClick={() => {
          try { localStorage.removeItem("gk"); } catch (e) {}
          window.location.href = "/signin";
        }}
      >
        Log In
      </a>
      <a href="/signup" className="logged-out-btn secondary">Create Account</a>
    </div>
  </div>
);

const MessageBubble = ({ messageId, message, isUser, isTyping, messageType, timestamp, dispatch, navigate, token, userId, onRetry, onGameLinkClick }) => {
  const [iframeDismissed, setIframeDismissed] = React.useState(false);
  const aiResponseRef = React.useRef(null);

  const iframePayload = React.useMemo(() => {
    if (messageType !== 'iframe') return null;
    try { return JSON.parse(message); } catch (_e) { return { url: message, title: 'Game' }; }
  }, [message, messageType]);

  // Handle game link clicks - detect isMiniGame attribute
  React.useEffect(() => {
    if (messageType !== 'airesponse' || !aiResponseRef.current) return;

    const handleGameLinkClick = (e) => {
      const link = e.target.closest('a[isMiniGame]');
      if (!link) return;

      // Check if this is a mini game link
      const isMiniGame = link.getAttribute('isMiniGame') === 'true';

      if (isMiniGame) {
        // Mini game: prevent default and launch directly (NO chatbot request)
        e.preventDefault();

        // Extract all game data from href URL parameters
        let href = link.getAttribute('href');
        // Clean up malformed href (remove %22 or extra quotes)
        href = href.replace(/%22/g, '').replace(/"\s*$/, '');

        try {
          const url = new URL(href, window.location.origin);
          const gameData = {
            gameCode: url.searchParams.get('gamecode'),
            gameName: url.searchParams.get('name'),
            aggregator: url.searchParams.get('aggregator'),
            provider: url.searchParams.get('provider'),
            gameId: url.searchParams.get('gamecode'),
            aggregatorType: url.searchParams.get('aggregator'),
            launchUrl: href
          };

          console.log('[Mini Game Link Click] Extracted data:', gameData);

          // Dispatch custom event with game data - will be handled by parent to launch game
          const event = new CustomEvent('mini-game-launch', {
            detail: { gameData, link }
          });
          document.dispatchEvent(event);
        } catch (_e) {
          console.error('Failed to parse game link:', _e);
        }
      }
      // If not isMiniGame, allow normal href navigation
    };

    const container = aiResponseRef.current;
    container.addEventListener('click', handleGameLinkClick);
    return () => container.removeEventListener('click', handleGameLinkClick);
  }, [messageType]);

  if (messageType === 'iframe') {
    if (iframeDismissed) return null;
    return (
      <div className="chat-message assistant iframe-message flex mb-4">
        <div className="message-content rounded-lg px-4 py-2">
          <IframeMessage
            url={iframePayload?.url}
            title={iframePayload?.title}
            onClose={() => setIframeDismissed(true)}
            dispatch={dispatch}
            token={token}
            isLoggedIn={token !== null}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-message ${isUser ? 'user' : 'assistant'} flex mb-4`}>
      <div className="message-content rounded-lg px-4 py-2">
        {isTyping || messageType === 'typing' ? (
          <div className="typing-indicator-wrapper">
            <div className="typing-status-text">Loading</div>
            <div className="typing-indicator"><span></span><span></span><span></span></div>
          </div>
        ) : messageType === 'error' ? (
          <ErrorMessage content={message} onRetry={onRetry} />
        ) : messageType === 'welcome' ? (
          <WelcomeMessage isLoggedIn={token !== null} />
        ) : messageType === 'data' ? (
          <div className="api-data-message">{renderApiDataMessage(message, dispatch, navigate, token, userId, messageId)}</div>
        ) : messageType === 'airesponse' ? (
          message && message.trim() ? (
            <div className="ai-response-display" ref={aiResponseRef}>
              <div className="ai-response-content" dangerouslySetInnerHTML={{ __html: formatAIResponseContent(message) }} />
            </div>
          ) : null
        ) : (
          <ReactMarkdown
            components={{
              ul: ({ children }) => <ul style={{ padding: 0, listStyle: 'none', margin: '8px 0' }}>{children}</ul>,
              ol: ({ children }) => <ol style={{ padding: 0, listStyle: 'none', margin: '8px 0' }}>{children}</ol>,
              li: ({ children }) => (
                <li className="md-list-item">
                  <ChevronRight size={13} className="md-list-icon" />
                  <span>{children}</span>
                </li>
              ),
            }}
          >{message}</ReactMarkdown>
        )}
        {timestamp && (
          <div className="msg-timestamp">
            {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        )}
      </div>
    </div>
  );
};

const AssistantChat = ({ onThemeChange, onClose, forceActive = false, forceAnonymous = false }) => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const reduxIsActive = useSelector(selectChatActive); // Always call useSelector at the top level
  const isActive  = forceActive ? true : reduxIsActive;
  const error     = useSelector(selectChatError);
  const userId    = useSelector(selectUserId);
  const userPhone = useSelector(selectUserPhone);
  const messages  = useSelector(selectChatMessages);
  const { balance, pin, userDetails, countryCode } = useSelector(state => state?.signIn || {});
  const { accountDetailsData } = useSelector(state => state?.accountDetails || {});

  const token = useMemo(() => { try { return getFromLocalStorage('gk'); } catch (_e) { return null; } }, []);

  const [anonymousSessionId, setAnonymousSessionId] = useState(() => {
    try {
      return sessionStorage.getItem('fasfas_anonymousSessionId') || null;
    } catch {
      return null;
    }
  });

  const localStorageBalance = useMemo(() => {
    try { return getFromLocalStorage('userBalance') || getFromLocalStorage('balance'); } catch (_e) { return null; }
  }, []);

  const userDetailsBalance = useMemo(() => {
    if (typeof accountDetailsData === 'object' && accountDetailsData) {
      return accountDetailsData.balance || accountDetailsData.walletBalance || accountDetailsData.real_balance?.result?.balance || null;
    }
    return null;
  }, [accountDetailsData]);

  const displayBalance = balance || localStorageBalance || userDetailsBalance || '0.00';
  const isLoggedIn = !!token;
  const formattedBalance = useMemo(() => {
    const n = typeof displayBalance === 'string' ? parseFloat(displayBalance) : displayBalance;
    return isNaN(n) ? '0.00' : n.toLocaleString();
  }, [displayBalance]);

  const [sessionExpired, setSessionExpired] = useState(false);

  const [accountDetailsFetched, setAccountDetailsFetched] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isLoadingchat, setIsLoading] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);

  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const quickActionsRef = useRef(null);
  const shouldForceScrollRef = useRef(false);

  useEffect(() => {
    if (userPhone) dispatch(updateUserIdAndReload(userPhone));
    else dispatch(reloadChatHistory());
    dispatch(initializeWelcomeMessage());
  }, [dispatch, userPhone]);

  useEffect(() => {
    if (userPhone && userPhone !== userId) dispatch(updateUserIdAndReload(userPhone));
  }, [userPhone, userId, dispatch]);

  useEffect(() => {
    if (token && !accountDetailsFetched) { dispatch(fetchAccountDetails(token)); setAccountDetailsFetched(true); }
  }, [token, accountDetailsFetched, dispatch]);

  // Refresh balance when user returns from game (page visibility changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isLoggedIn && token) {
        // User returned to the page - refresh balance to get updated account details
        dispatch(getRealBalance());
        dispatch(fetchAccountDetails(token));
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isLoggedIn, token, dispatch]);

  const callChatAPI = useCallback(async (message) => {
    const trimmedMessage = String(message).trim();

    let reqBody;
    if (!isLoggedIn) {
      let currentAnonSessionId = null;
      try { currentAnonSessionId = sessionStorage.getItem('fasfas_anonymousSessionId') || null; } catch {}
      reqBody = {
        message: trimmedMessage,
        userId: null,
        token: null,
        anonymousSessionId: currentAnonSessionId,
      };
    } else {
      // Generate clientId and sessionId for this request
      const clientId = Date.now().toString();
      const sessionId = Number(Date.now().toString());

      // Detect device type dynamically
      const isMobileDevice = /Mobile|Android|iPhone|iPad|iPod|Tablet|Windows Phone/.test(navigator.userAgent);
      const clientType = isMobileDevice ? 'mobile' : 'web';

      // Get user details from Redux
      const phoneNumber = userDetails?.userOtherInfo?.phone || userPhone || userId || '587';
      const userCountryCode = userDetails?.countryCode || countryCode || '254';
      const userEmail = userDetails?.userOtherInfo?.email || '';
      const msisdn = userCountryCode + phoneNumber;

      reqBody = {
        message: String(message).trim(),
        userId: String(userPhone || userId || '587'),
        token,
        // Always include payment/user details with every request
        // accessToken: token,
        clientType: clientType,
        mobileOperator: 'MPESA',
        msisdn: msisdn,
        email: userEmail,
        paymentOperator: 'MPESA',
        paymentType: 'CARD',
        pin: pin,
        clientId,
        sessionId,
      };
    }

    const response = await axios.post(CHAT_API_ENDPOINT, reqBody, {
      headers: { accept: '*/*', 'Content-Type': 'application/json' }, timeout: 30000
    });
    // If anonymous, persist the sessionId for subsequent requests
    if (!isLoggedIn && response.data?.anonymousSessionId) {
      try { sessionStorage.setItem('fasfas_anonymousSessionId', response.data.anonymousSessionId); } catch {}
      setAnonymousSessionId(response.data.anonymousSessionId);
    }
    return response.data;
  }, [token, userPhone, userId, isLoggedIn, pin, userDetails, countryCode]);

  const processResponse = useCallback((apiResponse) => {
    if (!apiResponse) return [{ id: uuidv4(), content: "It's taking longer. Please try again.", isUser: false, type: "text", timestamp: Date.now() }];
    const msgs = [];

    const pushReply = (replyText) => {
      if (!replyText || !replyText.trim()) return;
      // Avoid duplicate messages
      const isDuplicate = msgs.some(m => m.content === replyText);
      if (isDuplicate) return;
      msgs.push({
        id: uuidv4(),
        content: replyText,
        isUser: false,
        type: (replyText.includes('<') && replyText.includes('>')) ? 'airesponse' : 'text',
        timestamp: Date.now(),
      });
    };

    const pushDataMessage = (dataItems) => {
      if (!Array.isArray(dataItems) || !dataItems.length) return;
      // --- Check for error in data[0].data.error for messageType: "play_game" or similar ---
      const errorItem = dataItems.find(i => i?.data?.error);
      if (errorItem) {
        // Prefer showing the HTML reply if available, else show the plain error
        const errorContent = apiResponse.reply || errorItem.data.error;
        const isDuplicate = msgs.some(m => m.content === errorContent);
        if (!isDuplicate) {
          msgs.push({
            id: uuidv4(),
            content: errorContent,
            isUser: false,
            type: apiResponse.reply ? 'airesponse' : 'text',
            timestamp: Date.now(),
          });
        }
        return;
      }
      const bal = dataItems.find(i => i.messageType === 'get_balances');
      if (bal?.data?.wallet_balance) {
        localStorage.setItem('userBalance', String(bal.data.wallet_balance));
        window.dispatchEvent(new CustomEvent('balanceUpdated', { detail: String(bal.data.wallet_balance) }));
      }
      msgs.push({ id: uuidv4(), content: dataItems, isUser: false, type: 'data', timestamp: Date.now() });
    };

    const topLevelType = (apiResponse.messageType || '').toLowerCase();
    let handledCombined = false;

    // Check if there's an error in the response data
    const hasError = Array.isArray(apiResponse.data) && apiResponse.data.some(i => i?.data?.error);

    if (topLevelType === 'data') {
      const filterableData = Array.isArray(apiResponse.data)
        ? apiResponse.data.filter(item => !['list_mini_games', 'change_password', 'password_reset_generate_otp', 'password_reset_verify_otp', 'self_exclusion'].includes(item?.messageType))
        : apiResponse.data;
      if (filterableData?.length || !Array.isArray(filterableData)) {
        pushDataMessage(filterableData);
      }
    } else if (topLevelType === 'data_and_airesponse') {
      handledCombined = true;
      pushReply(apiResponse.reply);
      const filterableData = Array.isArray(apiResponse.data)
        ? apiResponse.data.filter(item => !['list_mini_games', 'change_password', 'password_reset_generate_otp', 'password_reset_verify_otp', 'self_exclusion'].includes(item?.messageType))
        : apiResponse.data;
      if (filterableData?.length || !Array.isArray(filterableData)) {
        pushDataMessage(filterableData);
      }
    } else if (topLevelType === 'airesponse' && Array.isArray(apiResponse.data) && apiResponse.data.length > 0) {
      handledCombined = true;
      pushReply(apiResponse.reply);
      const filterableData = apiResponse.data.filter(item => !['list_mini_games', 'change_password', 'password_reset_generate_otp', 'password_reset_verify_otp', 'self_exclusion', 'wallet_deposit', 'payment_config'].includes(item?.messageType));
      if (filterableData?.length) {
        pushDataMessage(filterableData);
      }
    }

    if (!handledCombined && topLevelType !== 'data' && Array.isArray(apiResponse.data)) {
      const combinedItems = apiResponse.data.filter(i => i?.messageType === 'data_and_airesponse');
      if (combinedItems.length) {
        handledCombined = true;
        combinedItems.forEach((item) => {
          pushReply(item?.reply);
          pushDataMessage(item?.data);
        });
        const remainingData = apiResponse.data.filter(i =>
          i?.messageType !== 'data_and_airesponse' &&
          !['list_mini_games', 'change_password', 'password_reset_generate_otp', 'password_reset_verify_otp'].includes(i?.messageType)
        );
        if (remainingData.length) {
          pushDataMessage(remainingData);
        }
      }
    }

    if (!handledCombined && topLevelType !== 'data') {
      // Only show success reply if there's no error
      if (!hasError) {
        pushReply(apiResponse.reply);
      }
      // Filter out non-renderable data types
      const renderableData = Array.isArray(apiResponse.data)
        ? apiResponse.data.filter(item => !['list_mini_games', 'change_password', 'password_reset_generate_otp', 'password_reset_verify_otp', 'self_exclusion'].includes(item?.messageType))
        : apiResponse.data;
      if (renderableData?.length || !Array.isArray(renderableData)) {
        pushDataMessage(renderableData);
      }
    }

    if (!msgs.length) msgs.push({ id: uuidv4(), content: 'I received your message. How can I help you further?', isUser: false, type: 'text', timestamp: Date.now() });
    return msgs;
  }, []);

  const dispatchError = useCallback((err, retryMessage = null) => {
    let code = 'generic';
    const status = err?.response?.status || (err?.message?.includes('500') ? 500 : null);
    const messageText = err?.response?.data?.message || err?.message || "";
    // --- SESSION EXPIRED HANDLING ---
    if (
      (status === 500 && typeof messageText === "string" && messageText.toLowerCase().includes("session expired")) ||
      (typeof err?.message === "string" && err.message.toLowerCase().includes("session expired"))
    ) {
      try { localStorage.removeItem("gk"); } catch (e) {}
      setSessionExpired(true);
      return; // Don't dispatch a generic error message
    }
    // --- END SESSION EXPIRED HANDLING ---
    if (status === 500) code = 500;
    else if (err.message?.includes('Network') || err.message?.includes('network')) code = 'network';
    else if (err.message?.includes('Authentication') || err.message?.includes('auth')) code = 'auth';
    else if (err.message?.includes('timed out') || err.message?.includes('timeout')) code = 'timeout';
    const meta = ERROR_META[code] || ERROR_META.generic;
    dispatch(addMessage({
      id: uuidv4(),
      content: { code, headline: meta.headline, description: meta.description, retryMessage },
      isUser: false,
      type: 'error',
      timestamp: Date.now(),
    }));
  }, [dispatch]);

  const greetingRe = /^(hi+|hello+|hey+|hiya|howdy|greetings|good\s*(morning|afternoon|evening)|what'?s\s*up|sup)[\s!?.]*$/i;

  // Handle game link clicks from AI suggestions
  const handleGameLinkClick = useCallback((gameName) => {
    const gameMessage = `play ${gameName}`;
    setInputValue(gameMessage);
    setIsLoading(true);
    shouldForceScrollRef.current = true;

    let newHistory = Array.isArray(messages) ? [...messages] : [];
    const userMsg = { id: uuidv4(), content: gameMessage, isUser: true, timestamp: Date.now(), type: 'text' };
    newHistory.push(userMsg);

    // Add loader/typing indicator as an assistant message
    const loaderMsg = { id: uuidv4(), content: '', isUser: false, type: 'typing', timestamp: Date.now() };
    newHistory.push(loaderMsg);

    dispatch(setMessages(trimChatHistory(newHistory)));

    (async () => {
      try {
        const res = await callChatAPI(gameMessage);

        if (res?.sessionId) {
          dispatch(setSessionId(res.sessionId));
        }

        const newMessages = processResponse(res);
        newHistory = newHistory.filter(msg => msg.type !== 'typing');
        newHistory = [...newHistory, ...newMessages];
        dispatch(setMessages(trimChatHistory(newHistory)));

      } catch (err) {
        const newMessages = processResponse(null);
        newHistory = newHistory.filter(msg => msg.type !== 'typing');
        newHistory = [...newHistory, ...newMessages];
        dispatchError(err, gameMessage);
        dispatch(setMessages(trimChatHistory(newHistory)));
      } finally {
        setIsLoading(false);
      }
    })();
  }, [messages, dispatch, callChatAPI, processResponse, dispatchError]);

  // Listen for mini game launches - use existing iframe window (like hasMiniGame=true)
  useEffect(() => {
    const handleMiniGameLaunch = (e) => {
      const { gameData } = e.detail;
      if (!gameData || !gameData.launchUrl) {
        console.error('[Mini Game] Missing game data or launchUrl');
        return;
      }

      console.log('[Mini Game] Launching with existing iframe. Game data:', gameData);

      // Create a play_game message using the EXISTING iframe rendering system
      const gameMessage = {
        id: uuidv4(),
        content: [
          {
            messageType: 'play_game',
            data: {
              game: {  // ✅ WRAP game object inside 'game' property
                gameId: gameData.gameCode || gameData.gameId || 'unknown',
                gameName: gameData.gameName || 'Game',
                aggregatorType: gameData.aggregatorType || gameData.aggregator || 'negames',
                gameCode: gameData.gameCode || 'unknown',
                provider: gameData.provider || 'negames',
                launchUrl: gameData.launchUrl,
                minStake: 1,
                maxStake: 10000,
                minBet: 1,
                maxBet: 10000,
                freeBet: 0
              }
            }
          }
        ],
        isUser: false,
        type: 'data',
        hasMiniGame: true,
        timestamp: Date.now()
      };

      console.log('[Mini Game] Message created:', gameMessage);
      console.log('[Mini Game] Message content structure:', {
        messageType: gameMessage.type,
        hasMiniGame: gameMessage.hasMiniGame,
        contentArray: gameMessage.content,
        firstItem: gameMessage.content?.[0],
        gameData: gameMessage.content?.[0]?.data
      });

      // Add the game launch message directly to chat
      let newHistory = Array.isArray(messages) ? [...messages] : [];
      newHistory.push(gameMessage);
      dispatch(setMessages(trimChatHistory(newHistory)));
      shouldForceScrollRef.current = true;
    };

    document.addEventListener('mini-game-launch', handleMiniGameLaunch);
    return () => document.removeEventListener('mini-game-launch', handleMiniGameLaunch);
  }, [messages, dispatch]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    const message = inputValue.trim();
    if (!message || isLoadingchat || sessionExpired) return;
    setInputValue('');
    setIsLoading(true);
    shouldForceScrollRef.current = true;

    let newHistory = Array.isArray(messages) ? [...messages] : [];
    const userMsg = { id: uuidv4(), content: message, isUser: true, timestamp: Date.now(), type: 'text' };
    newHistory.push(userMsg);

    // Add loader/typing indicator as an assistant message
    const loaderMsg = { id: uuidv4(), content: '', isUser: false, type: 'typing', timestamp: Date.now() };
    newHistory.push(loaderMsg);

    dispatch(setMessages(trimChatHistory(newHistory)));

    if (greetingRe.test(message)) {
      // Replace loader with welcome message
      const welcomeMsg = { id: uuidv4(), content: '', isUser: false, type: 'welcome', timestamp: Date.now() }; // <-- define welcomeMsg
      newHistory = newHistory.slice(0, -1); // Remove loader
      newHistory.push(welcomeMsg);
      dispatch(setMessages(trimChatHistory(newHistory)));
      setIsLoading(false);
      return;
    }

    try {
      const res = await callChatAPI(message);

      if (
        res?.status === 500 &&
        typeof res?.message === "string" &&
        res.message.toLowerCase().includes("session expired")
      ) {
        try { localStorage.removeItem("gk"); } catch (e) {}
        setSessionExpired(true);
        setIsLoading(false);
        return;
      }

      if (res?.sessionId) {
        dispatch(setSessionId(res.sessionId));
      }

      const newMessages = processResponse(res);

      // Remove loader (any message with type: 'typing') before adding assistant responses
      newHistory = newHistory.filter(msg => msg.type !== 'typing');
      newHistory = [...newHistory, ...newMessages];
      dispatch(setMessages(trimChatHistory(newHistory)));

    } catch (err) {
      // Remove loader and add error message
      const newMessages = processResponse(null);
        newHistory = newHistory.filter(msg => msg.type !== 'typing');
        newHistory = [...newHistory, ...newMessages];
      dispatchError(err, message);
      dispatch(setMessages(trimChatHistory(newHistory)));
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoadingchat, callChatAPI, processResponse, dispatchError, dispatch, sessionExpired, messages]);

  const sendQuickMessage = useCallback(async (text) => {
    const message = text.trim();
    if (!message || isLoadingchat) return;
    setIsLoading(true);
    shouldForceScrollRef.current = true;

    let newHistory = [];
    if (Array.isArray(messages)) {
      newHistory = [...messages];
    }
    const userMsg = { id: uuidv4(), content: message, isUser: true, timestamp: Date.now(), type: 'text' };
    newHistory.push(userMsg);

    // Add loader/typing indicator as an assistant message
    const loaderMsg = { id: uuidv4(), content: '', isUser: false, type: 'typing', timestamp: Date.now() };
    newHistory.push(loaderMsg);

    dispatch(setMessages(trimChatHistory(newHistory)));

    if (greetingRe.test(message)) {
      // Replace loader with welcome message
      const welcomeMsg = { id: uuidv4(), content: '', isUser: false, type: 'welcome', timestamp: Date.now() };
      newHistory = newHistory.slice(0, -1); // Remove loader
      newHistory.push(welcomeMsg);
      dispatch(setMessages(trimChatHistory(newHistory)));
      setIsLoading(false);
      return;
    }
    try {
      const res = await callChatAPI(message);
      if (res?.sessionId) {
        dispatch(setSessionId(res.sessionId));
      }
      const newMessages = processResponse(res);

      // Remove loader (any message with type: 'typing') before adding assistant responses
      newHistory = newHistory.filter(msg => msg.type !== 'typing');
      newHistory = [...newHistory, ...newMessages];
      dispatch(setMessages(trimChatHistory(newHistory)));
    } catch (err) {
      // Remove loader and add error message
       const newMessages = processResponse(null);
        newHistory = newHistory.filter(msg => msg.type !== 'typing');
        newHistory = [...newHistory, ...newMessages];
      dispatchError(err, message);
      dispatch(setMessages(trimChatHistory(newHistory)));
    } finally {
      setIsLoading(false);
    }
  }, [isLoadingchat, callChatAPI, processResponse, dispatchError, dispatch, messages]);

  const handleDropdownToggle = () => setIsDropdownOpen(p => !p);
  const handleQuickActionsToggle = () => setIsQuickActionsOpen(p => !p);

  useEffect(() => {
    shouldForceScrollRef.current = false;

    const scrollToNewMessage = () => {
      const c = messagesContainerRef.current;
      if (!c) return;

      // Get all messages (user message + AI response)
      const messageElements = c.querySelectorAll('.chat-message');
      if (messageElements.length < 2) {
        // If less than 2 messages, scroll to bottom
        c.scrollTop = c.scrollHeight;
        return;
      }

      // Get the SECOND-to-last message (the user's request before the latest AI response)
      const userMessageIndex = messageElements.length - 2;
      const userMessage = messageElements[userMessageIndex];

      if (userMessage) {
        // Scroll to position the user message with more visible context below
        // This centers the current conversation nicely on screen
        const messageTop = userMessage.offsetTop;
        const containerHeight = c.clientHeight;

        // Position user message about 1/4 down the viewport so response is visible below
        const targetScroll = Math.max(0, messageTop - (containerHeight / 4));

        console.log('[SCROLL] ✅ Centering conversation view:', {
          userMessageTop: messageTop,
          containerHeight,
          targetScroll,
          viewportPosition: '1/4 from top'
        });

        c.scrollTop = targetScroll;
      }
    };

    // Scroll with retries for content rendering
    setTimeout(scrollToNewMessage, 50);
    setTimeout(scrollToNewMessage, 150);
    setTimeout(scrollToNewMessage, 400);
  }, [messages, isLoadingchat]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const h = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsDropdownOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isQuickActionsOpen) return;
    const h = (e) => { if (quickActionsRef.current && !quickActionsRef.current.contains(e.target)) setIsQuickActionsOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [isQuickActionsOpen]);

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isActive && isLoggedIn) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isActive, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Call DELETE API whenever chat is opened (chatOpenCount changes)
  const chatOpenCount = useSelector(selectChatOpenCount);
  console.log('AssistantChat rendered');
  console.log('chatOpenCount:', chatOpenCount);

  useEffect(() => {
    console.log('DELETE useEffect triggered', { chatOpenCount, userId, token });
    // Only delete history for logged-in users (not guest users)
    if (userId && token && !userId.startsWith('guest_')) {
      console.log('DELETE API called:', `${DELETE_CHAT_HISTORY_API}?userId=${userId}&token=${token}`);
      axios.delete(`${DELETE_CHAT_HISTORY_API}?userId=${userId}&token=${token}`, {
        headers: { accept: '*/*' }
      }).catch(() => {});
    }
  }, [chatOpenCount, userId, token]);
  
  // Re-focus input after AI response completes
  // Focus works for both logged-in and anonymous users
  useEffect(() => {
    if (!isLoadingchat && isActive) {
      inputRef.current?.focus();
    }
  }, [isLoadingchat, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isActive) return null;

  return (
    <div className={`chat-container flex flex-col h-full ${isDarkTheme ? 'dark' : 'light'}`}>
      <div className="chat-header flex items-center justify-between">
        <div className="header-left flex items-center gap-2">
          {onClose && (
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-color)', display: 'flex', alignItems: 'center', padding: '4px' }}
              aria-label="Close chat"
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><line x1="5" y1="5" x2="17" y2="17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/><line x1="17" y1="5" x2="5" y2="17" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>
            </button>
          )}
          <div className="chat-icon flex items-center justify-center">
            <img src="/favicon.png" alt="FasFas AI" className="chat-icon-img w-8 h-8 object-contain" />
          </div>
          <div className="chat-header-text flex flex-col">
            <h3 className="chat-title text-sm font-bold">AI Assist</h3>
            <span className="chat-subtitle text-xs opacity-80">Online</span>
          </div>
        </div>
        <div className="header-actions relative" ref={dropdownRef}>
          {!isLoggedIn ? (
            <>
              <button className="btn-signin flex items-center gap-2 px-3 py-2 rounded-lg" onClick={() => navigate('/signin')}>
                <UserCheck size={15} />
                <span className="signin-text font-semibold">Sign In</span>
              </button>
              <button className="btn-balance flex items-center gap-2 px-3 py-2 rounded-lg" onClick={handleDropdownToggle}>
                <Settings size={16} />
              </button>
            </>
          ) : (
          <button className="btn-balance flex items-center gap-2 px-3 py-2 rounded-lg" onClick={handleDropdownToggle}>
            <span className="balance-text font-semibold">KES {formattedBalance}</span>
            <Settings size={16} />
          </button>
          )}
          {isDropdownOpen && (
            <div className="dropdown-menu absolute right-0 mt-2 rounded-lg shadow-lg overflow-hidden z-50">
              <button className="dropdown-item flex items-center gap-3 w-full px-4 py-3" onClick={() => { dispatch(clearChat()); dispatch(initializeWelcomeMessage()); setIsDropdownOpen(false); }}>
                <Trash2 size={18} /><span>Clear chat</span>
              </button>
              <button className="dropdown-item theme-toggle-item flex items-center w-full px-4 py-3" onClick={() => { const n = !isDarkTheme; setIsDarkTheme(n); if (onThemeChange) onThemeChange(n ? 'dark' : 'light'); }}>
                <div className="theme-toggle-content flex items-center justify-between w-full gap-3">
                  <span className="theme-label font-medium">Theme</span>
                  <div className="theme-toggle-switch flex items-center gap-2">
                    <Sun size={14} />
                    <div className={`toggle-slider relative ${isDarkTheme ? 'dark' : 'light'}`}><div className="toggle-thumb absolute transition-transform"></div></div>
                    <Moon size={14} />
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      {!sessionExpired && error && (
        <div className="chat-error-banner flex items-center justify-between px-4 py-3">
          <span className="error-text text-sm flex-1">{error}</span>
          <button className="error-close ml-2 text-xl" onClick={() => dispatch(clearError())}>×</button>
        </div>
      )}
      <div className="chat-messages flex-1 overflow-y-auto" ref={messagesContainerRef}>
        <div className="api-messages-section" style={{ display: 'flex', flexDirection: 'column' }}>
          {!sessionExpired && messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              messageId={msg.id}
              message={msg.content}
              isUser={msg.isUser}
              messageType={msg.type || 'text'}
              timestamp={msg.timestamp}
              isTyping={false}
              dispatch={dispatch}
              navigate={navigate}
              token={token}
              userId={userId}
              onRetry={sendQuickMessage}
              onGameLinkClick={handleGameLinkClick}
            />
          ))}
          {sessionExpired && <LoggedOutBanner />}
        </div>
      </div>
      <form
        className="chat-input-form flex-shrink-0"
        onSubmit={handleSendMessage}
      >
        <div className="input-wrapper flex items-center gap-2">
          <div className="input-actions relative" ref={quickActionsRef}>
            <button
              type="button"
              className={`btn-action${isQuickActionsOpen ? ' active' : ''}`}
              onClick={handleQuickActionsToggle}
              title="Quick actions"
            >
              <Plus size={16} />
            </button>
            {isQuickActionsOpen && (
              <div className="quick-start-modal">
                <div className="quick-start-header">
                  <Sparkles size={13} /> Quick Actions
                </div>
                <div className="quick-start-list">
                  {(isLoggedIn ? WELCOME_ITEMS_AFTER_LOGIN : WELCOME_ITEMS_BEFORE_LOGIN).map(({ icon: Icon, shortLabel, actionText }, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="quick-start-item"
                      onClick={() => {
                        setIsQuickActionsOpen(false);
                        sendQuickMessage(actionText);
                      }}
                    >
                      <Icon size={13} />
                      <span>{shortLabel}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <input
            type="text"
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            placeholder="Ask me anything..."
            className="chat-input flex-1 px-4 py-3 rounded-lg focus:outline-none"
            disabled={isLoadingchat}
            maxLength={1000}
          />
          <button
            type="submit"
            className="btn-send flex items-center justify-center p-3 rounded-lg disabled:opacity-50"
            disabled={isLoadingchat || !inputValue.trim()}
            title={isLoadingchat ? "Sending..." : "Send message"}
          >
            {isLoadingchat ? (
              <div className="SB-loader-container">
                <div className="SB-loader"></div>
              </div>
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
        {/* Beta Disclaimer Banner */}
        <div className="chat-beta-banner">
          <span className="beta-text">⚠ FasFas Assist is AI and still learning. Please double-check before taking actions.</span>
        </div>
      </form>
    </div>
  );
};

function trimChatHistory(messages) {
  // Find all user message indexes
  const userMsgIndexes = messages
    .map((msg, idx) => (msg.isUser ? idx : null))
    .filter(idx => idx !== null);

  // If <= 10 user messages, keep all
  if (userMsgIndexes.length <= 10) return messages;

  // Find the index of the 10th from last user message
  const firstUserToKeepIdx = userMsgIndexes[userMsgIndexes.length - 10];

  // Keep all messages from firstUserToKeepIdx to the end
  return messages.slice(firstUserToKeepIdx);
}

// ── Providers List Renderer ──────────────────────────────────────────────────
const PROVIDER_ICONS = {
  endorphina: Sparkles,
  hacksaw_gaming: Flame,
  pigaboom: Gift,
  ezugi: Trophy,
  stp: Gamepad2,
  negames: Play,
  gridloop: Clock,
  nolimit_city: ShieldCheck,
  booming: Coins,
  turbogames: Flame,
};

const prettifyProvider = (name) => {
  if (!name) return '';
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const renderProvidersList = (data, key) => {
  const providers = data.providers || [];
  return (
    <div key={key} className="providers-list-display">
      <div className="providers-list-header">
        <Sparkles size={18} className="providers-list-icon" />
        <h3 className="providers-list-title">Game Providers</h3>
      </div>
      <ul className="providers-list">
        {providers.map((prov) => {
          const Icon = PROVIDER_ICONS[prov] || Gamepad2;
          return (
            <li key={prov} className="provider-list-item flex items-center gap-2">
              {/* <Icon size={16} /> */}
              <span>{prettifyProvider(prov)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ── Categories List Renderer ─────────────────────────────────────────────────
const CATEGORY_ICONS = {
  casino: Trophy,
  // multidice: Dice,
  slot: Play,
  matchall: Sparkles,
  cash: Coins,
  videoslot: Play,
  bet_on_poker: Gamepad2,
  unknown: AlertTriangle,
  balldrop: Gift,
  xponential: Flame,
};

const prettifyCategory = (name) => {
  if (!name) return '';
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

const renderCategoriesList = (data, key) => {
  const categories = data.categories || [];
  return (
    <div key={key} className="categories-list-display">
      <div className="categories-list-header">
        <Gamepad2 size={18} className="categories-list-icon" />
        <h3 className="categories-list-title">Game Categories</h3>
      </div>
      <ul className="categories-list">
        {categories.map((cat) => {
          const Icon = CATEGORY_ICONS[cat.toLowerCase()] || Gamepad2;
          return (
            <li key={cat} className="category-list-item flex items-center gap-2">
              {/* <Icon size={16} /> */}
              <span>{prettifyCategory(cat)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ── Deposit/Withdraw History Renderer ────────────────────────────────────────
const formatTransactionDate = (isoString) => {
  if (!isoString) return '';
  try {
    const d = new Date(isoString);
    return d.toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoString;
  }
};

const renderDepositWithdrawHistory = (data, key) => {
  if (!data || !Array.isArray(data.data) || data.data.length === 0) {
    const isWithdraw = data?.paymentType === 'WITHDRAW';
    return (
      <div key={key} className="dwh-empty">
        <span className="dwh-empty-icon">{isWithdraw ? <CreditCard size={28} /> : <Wallet size={28} />}</span>
        <p className="dwh-empty-title">No {isWithdraw ? 'Withdrawals' : 'Deposits'} Yet</p>
        <p className="dwh-empty-sub">Your {isWithdraw ? 'withdrawal' : 'deposit'} history will appear here.</p>
      </div>
    );
  }
  const isDeposit = data.paymentType === 'DEPOSIT';
  return (
    <div key={key} className="dwh-container">
      <div className="dwh-header">
        <span className="dwh-header-icon">{isDeposit ? <Wallet size={16} /> : <CreditCard size={16} />}</span>
        <div className="dwh-title">{isDeposit ? 'Deposit History' : 'Withdrawal History'}</div>
      </div>
      <div className="dwh-list">
        {data.data.map((tx) => (
          <div key={tx.id} className={`dwh-item ${isDeposit ? 'deposit' : 'withdraw'}`}>
            <div className="dwh-item-top">
              <span className="dwh-ref">{`ID ${tx.id}`}</span>
              <span className={`dwh-status ${tx.status?.toLowerCase()}`}>{tx.status}</span>
            </div>
            <div className="dwh-item-bottom">
              <span className="dwh-date"><Clock size={11} /> {formatTransactionDate(tx.createdDate)}</span>
              <span className={`dwh-amount ${isDeposit ? 'deposit' : 'withdraw'}`}>
                {tx.amount < 0 ? '' : ''}KES {Math.abs(Number(tx.amount)).toLocaleString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssistantChat;